import { DEV } from "solid-js"
import { type Box3, type BufferGeometry, BufferAttribute } from "three"
import Unexpected from "../../Exception/Unexpected.class"
import { dummyPosition } from "../objects/DummyObject3D/dummyObject3D"

export enum UVProjectionPlane {
  XY = 1,
  XZ = 2,
  YZ = 3,
}

// --- 2️⃣ Define the function
export default function generateUVs(
  geometry: BufferGeometry,
  plane: UVProjectionPlane = UVProjectionPlane.XY,
  boundingBox?: Box3,
) {
  if (!boundingBox) {
    geometry.computeBoundingBox()
    boundingBox = geometry.boundingBox!
    if (DEV && !boundingBox) {
      throw new Unexpected("!boundingBox")
    }
  }

  const pos = geometry.attributes.position
  const uv = new Float32Array(pos.count * 2)

  const min = boundingBox!.min
  const max = boundingBox!.max

  // Dummy vector for size calculation
  const size = dummyPosition.subVectors(max, min)

  // --- 3️⃣ Project depending on chosen plane
  for (let i = 0; i < pos.count; i++) {
    let u = 0
    let v = 0

    switch (plane) {
      case UVProjectionPlane.XY: {
        const x = pos.getX(i)
        const y = pos.getY(i)
        u = (x - min.x) / size.x
        v = (y - min.y) / size.y
        break
      }

      case UVProjectionPlane.XZ: {
        const x = pos.getX(i)
        const z = pos.getZ(i)
        u = (x - min.x) / size.x
        v = (z - min.z) / size.z
        break
      }

      case UVProjectionPlane.YZ: {
        const y = pos.getY(i)
        const z = pos.getZ(i)
        u = (y - min.y) / size.y
        v = (z - min.z) / size.z
        break
      }

      default:
        throw new Unexpected(`Unsupported UVProjectionPlane: ${plane}`)
    }

    uv[i * 2] = u
    uv[i * 2 + 1] = v
  }

  geometry.setAttribute("uv", new BufferAttribute(uv, 2))
  geometry.attributes.uv.needsUpdate = true

  return geometry
}